/**
 * ExperimentDetailPage — variants, posterior time series (P(best) per
 * variant from the hourly stats sweep), latest snapshot table, decision
 * log, and lifecycle controls. Observational (channel-rotated)
 * experiments carry a persistent directional-only banner — the UI never
 * presents them as causal wins.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  useAdoptExperiment,
  useAdoptExperimentLifecycle,
  useAdoptExperimentResults,
} from '../hooks/useAdvertisingOptimization';
import { EvidenceClassBadge } from './ExperimentsPage';

interface Props {
  holderId: string;
}

/**
 * Categorical series colors — validated palette (dataviz skill reference
 * instance), assigned to variants in fixed variant order, never cycled.
 */
const SERIES_COLORS = [
  '#2a78d6', // blue
  '#008300', // green
  '#e87ba4', // magenta
  '#eda100', // yellow
  '#1baf7a', // aqua
  '#eb6834', // orange
  '#4a3aa7', // violet
  '#e34948', // red
] as const;

const LIFECYCLE_ACTIONS: Array<{
  verb: 'start' | 'stop' | 'complete' | 'archive';
  label: string;
  from: string[];
}> = [
  { verb: 'start', label: 'Start', from: ['draft', 'paused'] },
  { verb: 'stop', label: 'Pause', from: ['running'] },
  { verb: 'complete', label: 'Complete', from: ['running', 'paused'] },
  { verb: 'archive', label: 'Archive', from: ['draft', 'paused', 'completed'] },
];

export function ExperimentDetailPage({ holderId }: Props) {
  const { experimentId } = useParams<{ experimentId: string }>();
  const detailQuery = useAdoptExperiment(holderId, experimentId);
  const resultsQuery = useAdoptExperimentResults(holderId, experimentId);
  const lifecycle = useAdoptExperimentLifecycle(holderId, experimentId ?? '');

  const variants = useMemo(() => detailQuery.data?.variants ?? [], [detailQuery.data]);
  const results = useMemo(() => resultsQuery.data?.results ?? [], [resultsQuery.data]);

  // Pivot snapshot rows into one chart point per computed_at with a
  // pBest column per variant. Results arrive newest-first; the chart
  // reads oldest→newest.
  const series = useMemo(() => {
    const byTime = new Map<string, Record<string, number | string>>();
    for (const row of results) {
      const t = new Date(row.computedAt).toISOString();
      if (!byTime.has(t)) byTime.set(t, { computedAt: t });
      byTime.get(t)![row.variantKey] = row.pBest;
    }
    return [...byTime.values()].sort((a, b) =>
      String(a.computedAt).localeCompare(String(b.computedAt)),
    );
  }, [results]);

  const latestByVariant = useMemo(() => {
    const latest = new Map<string, (typeof results)[number]>();
    for (const row of results) {
      const existing = latest.get(row.variantKey);
      if (!existing || row.computedAt > existing.computedAt) latest.set(row.variantKey, row);
    }
    return latest;
  }, [results]);

  if (detailQuery.isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!detailQuery.data)
    return <div className="p-6 text-muted-foreground">Experiment not found.</div>;

  const { experiment } = detailQuery.data;
  const observational = experiment.evidenceClass === 'observational';

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <header className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">{experiment.name}</h1>
            <EvidenceClassBadge evidenceClass={experiment.evidenceClass} />
          </div>
          <p className="text-sm text-muted-foreground capitalize">
            {experiment.stage.replace('_', ' ')} · {experiment.status} · {experiment.trafficPct}%
            traffic · {experiment.denominatorPolicy.replace(/_/g, ' ')}
          </p>
          {experiment.hypothesis && (
            <p className="text-sm text-muted-foreground">“{experiment.hypothesis}”</p>
          )}
        </div>
        <div className="flex gap-2">
          {LIFECYCLE_ACTIONS.filter((a) => a.from.includes(experiment.status)).map((a) => (
            <button
              key={a.verb}
              onClick={() => lifecycle.mutate(a.verb)}
              disabled={lifecycle.isPending}
              className={`px-3 py-1.5 rounded-md text-sm disabled:opacity-50 ${
                a.verb === 'start' ? 'bg-primary text-primary-foreground' : 'border border-border'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </header>

      {lifecycle.isError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {lifecycle.error instanceof Error ? lifecycle.error.message : 'Action failed'}
        </div>
      )}

      {observational && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Directional comparison only: the ad channel's delivery optimization decides who sees each
          creative, so these differences carry selection bias and are not causal evidence. No winner
          is declared from this data.
        </div>
      )}

      <section className="rounded-lg border border-border p-4">
        <h2 className="font-medium mb-2">
          {observational ? 'Directional P(best) over time' : 'P(best) over time'}
        </h2>
        {series.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No posterior snapshots yet — the stats sweep runs hourly once the experiment is running
            and receiving exposures.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="currentColor" strokeOpacity={0.12} />
              <XAxis
                dataKey="computedAt"
                tickFormatter={(t: string) =>
                  new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                }
                stroke="currentColor"
                strokeOpacity={0.35}
                fontSize={11}
              />
              <YAxis
                domain={[0, 1]}
                tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                stroke="currentColor"
                strokeOpacity={0.35}
                fontSize={11}
                width={40}
              />
              <Tooltip
                formatter={(value: number) => `${(value * 100).toFixed(1)}%`}
                labelFormatter={(t: string) => new Date(t).toLocaleString()}
              />
              <Legend />
              {variants.map((v, i) => (
                <Line
                  key={v.variantKey}
                  type="monotone"
                  dataKey={v.variantKey}
                  name={v.name}
                  stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </section>

      <section className="rounded-lg border border-border p-4 overflow-x-auto">
        <h2 className="font-medium mb-2">Latest snapshot</h2>
        {latestByVariant.size === 0 ? (
          <p className="text-sm text-muted-foreground">No data yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1.5 pr-3 font-normal">Variant</th>
                <th className="py-1.5 pr-3 font-normal">Exposed</th>
                <th className="py-1.5 pr-3 font-normal">Conversions</th>
                <th className="py-1.5 pr-3 font-normal">Rate (95% CI)</th>
                <th className="py-1.5 pr-3 font-normal">P(best)</th>
                <th className="py-1.5 font-normal">Expected loss</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v, i) => {
                const row = latestByVariant.get(v.variantKey);
                const rate =
                  row && row.exposedUnits > 0 ? row.conversions / row.exposedUnits : null;
                return (
                  <tr key={v.variantKey} className="border-t border-border">
                    <td className="py-1.5 pr-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length] }}
                          aria-hidden
                        />
                        {v.name}
                        {v.isControl && (
                          <span className="text-xs text-muted-foreground">(control)</span>
                        )}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3">{row?.exposedUnits ?? '—'}</td>
                    <td className="py-1.5 pr-3">{row?.conversions ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      {rate !== null ? `${(rate * 100).toFixed(2)}%` : '—'}
                      {row?.ciLow != null && row?.ciHigh != null
                        ? ` (${(row.ciLow * 100).toFixed(1)}–${(row.ciHigh * 100).toFixed(1)}%)`
                        : ''}
                    </td>
                    <td className="py-1.5 pr-3">
                      {row ? `${(row.pBest * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="py-1.5">{row ? row.expectedLoss.toFixed(4) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {(resultsQuery.data?.decisions ?? []).length > 0 && (
        <section className="rounded-lg border border-border p-4">
          <h2 className="font-medium mb-2">Decision log</h2>
          <ul className="flex flex-col gap-1 text-sm">
            {(resultsQuery.data?.decisions ?? []).map((d, i) => (
              <li key={i} className="text-muted-foreground">
                <span className="text-foreground font-medium">{String(d.decision ?? '')}</span>
                {d.winner_variant_key ? ` → ${String(d.winner_variant_key)}` : ''} ·{' '}
                {String(d.decided_by ?? '')} ·{' '}
                {d.decided_at ? new Date(String(d.decided_at)).toLocaleString() : ''}
                {d.rationale ? ` — ${String(d.rationale)}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
